import { MapTileFormats } from "../../enums/maps/mapTileFormats.enum";

//The existing tile schemes.
type TileScheme = MapTileFormats;

//The tiles which this utility class supports.
//If new tile type is added, it should be included here ONLY after this utility class
//has implemented a URL builder for it.
//The below definition is a bit verbose compared to a simple type declaration like:
//type AllowedTileScheme = | MapTileFormats.XYZ | MapTileFormats.TMS | MapTileFormats.Quadkey | MapTileFormats.WMTS
//but this way we can iterate over them.
const ALLOWED_TILE_SCHEMES = [MapTileFormats.XYZ, MapTileFormats.Quadkey, MapTileFormats.TMS, MapTileFormats.WMTS] as const satisfies readonly TileScheme[];
type AllowedTileScheme = typeof ALLOWED_TILE_SCHEMES[number];

// As we don't want this class to be tight coupled with google.maps or any other mapping provider's library
// we define our own coordinate type.
export type Coord = { x: number; y: number };

export type CommonOptions = {
  // If your provider supports subdomains like a,b,c: use {S} in template and pass subdomains: ['a', 'b', 'c']
  subdomains?: string[];

  // Whether to wrap X across the world (so x can exceed 2^z - 1). Default: true
  wrapX?: boolean;

  // If provided, these will be used to also substitute custom tokens in the template
  // Example: extraParams: { Layer: 'myLayer', TileMatrixSet: 'WebMercatorQuad' }
  extraParams?: Record<string, string | number>;

  // If a provider requires clamping WGS84 tile Y range. Usually not needed for Google Maps.
  clampY?: boolean;
  maxZoom?: number;
  minZoom?: number;
};

export type WMTSOptions = CommonOptions & {
  // WMTS-specific options
  // Many WMTS servers use "top-left origin" (no Y flip). If false, uses TMS-like Y flip.
  // True by default
  yOriginTopLeft?: boolean;

  // How tile matrix ID is formed. Defaults to z.toString()
  tileMatrixId?: (z: number) => string;
};



/**
 * Utility class for generating tile URLs for various map tile schemes.
 *
 * The `TileUrlBuilder` provides static methods to construct tile URL templates for different
 * tiling schemes, including XYZ, TMS, Quadkey (Bing Maps), and WMTS. It supports token
 * replacement in URL templates, subdomain selection for load balancing, and coordinate
 * normalization (wrapping, clamping, flipping Y, etc.).
 *
 * ## Supported Tile Schemes
 * - `XYZ`: Standard web mercator tiling (top-left origin).
 * - `TMS`: Tile Map Service (bottom-left origin).
 * - `Quadkey`: Bing Maps quadkey addressing.
 * - `WMTS`: Web Map Tile Service, with flexible matrix and row/column options.
 *
 * ## Usage
 * Use `TileUrlBuilder.createGetTileUrl()` to generate a callback suitable for use as a
 * `getTileUrl` function in mapping libraries (e.g., Google Maps, Leaflet, OpenLayers).
 * Alternatively, use the direct helpers (`xyz`, `tms`, `quadkey`, `wmts`) for custom logic.
 *
 * ## Template Tokens
 * The following tokens can be used in URL templates:
 * - `{X}`: Tile X coordinate
 * - `{Y}`: Tile Y coordinate (may be flipped for TMS)
 * - `{Z}`: Zoom level
 * - `{Q}`: Quadkey (for Quadkey scheme)
 * - `{TileMatrix}`: WMTS tile matrix identifier
 * - `{TileRow}`: WMTS tile row
 * - `{TileCol}`: WMTS tile column
 * - `{S}`: Subdomain (for load balancing)
 * - Additional tokens can be provided via `extraParams` in options.
 *
 * ## Options
 * - `wrapX`: Whether to wrap X coordinates horizontally (default: true)
 * - `clampY`: Whether to clamp Y coordinates to valid range (default: false)
 * - `subdomains`: Array of subdomain strings for load balancing
 * - `extraParams`: Additional key-value pairs for template replacement
 * - `tileMatrixId`: Function to generate WMTS tile matrix identifier
 * - `yOriginTopLeft`: For WMTS, whether Y origin is top-left (default: true)
 * - `minZoom`/`maxZoom`: Optional zoom guards (placeholders for future use)
 *
 * @example
 * const getTileUrl = TileUrlBuilder.createGetTileUrl('XYZ', 'https://tileserver/{Z}/{X}/{Y}.png', {
 *   subdomains: ['a', 'b', 'c'],
 *   extraParams: { lang: 'en' }
 * });
 * const url = getTileUrl({ x: 1, y: 2 }, 3);
 */
export class TileUrlBuilder {


  /**
   * Factory for Google Maps: returns the getTileUrl callback
   * Creates a tile URL builder function based on the specified tile scheme and template.
   *
   * @param scheme - The tile scheme to use for URL generation. Supported schemes are 'XYZ', 'TMS', 'Quadkey', and 'WMTS'.
   * @param template - The URL template string with placeholders for tile coordinates and zoom level.
   * @param options - Optional configuration options specific to the chosen tile scheme.
   *                  For 'XYZ', 'TMS', and 'Quadkey', provide `CommonOptions`.
   *                  For 'WMTS', provide `WMTSOptions`.
   * @returns A function that takes a tile coordinate (`coord`) and zoom level (`zoom`), and returns the corresponding tile URL as a string.
   */
  public static createGetTileUrl(scheme: AllowedTileScheme, template: string, options: CommonOptions | WMTSOptions = {}): (coord: Coord, zoom: number) => string {
    const fnMap: Record<AllowedTileScheme, (coord: Coord, zoom: number) => string> = {
      [MapTileFormats.XYZ]: (c, z) => this.xyz(template, c, z, options as CommonOptions),
      [MapTileFormats.TMS]: (c, z) => this.tms(template, c, z, options as CommonOptions),
      [MapTileFormats.Quadkey]: (c, z) => this.quadkey(template, c, z, options as CommonOptions),
      [MapTileFormats.WMTS]: (c, z) => this.wmts(template, c, z, options as WMTSOptions),
    };

    const urlBuilderFn = fnMap[scheme];

    return (coord: Coord, zoom: number) => urlBuilderFn(coord, zoom);
  }

  public static xyz(template: string, coord: Coord, zoom: number, options: CommonOptions = {}): string {
    const { x, y } = this.normalizeCoord(coord, zoom, options);
    const tokens = {
      X: x,
      Y: y,
      Z: zoom,
      S: this.pickSubdomain(x, y, zoom, options.subdomains),
      ...this.kvToUpper(options.extraParams),
    };

    const sanitizedTemplate = this.sanitizeTemplate(template);
    return this.replaceTokens(sanitizedTemplate, tokens);
  }

  public static tms(template: string, coord: Coord, zoom: number, options: CommonOptions = {}): string {
    const { x, y } = this.normalizeCoord(coord, zoom, options);
    const yTms = this.flipY(y, zoom); // TMS uses bottom-left origin: flip Y from XYZ
    const tokens = {
      X: x,
      Y: yTms,
      Z: zoom,
      S: this.pickSubdomain(x, yTms, zoom, options.subdomains),
      ...this.kvToUpper(options.extraParams),
    };

    const sanitizedTemplate = this.sanitizeTemplate(template);
    return this.replaceTokens(sanitizedTemplate, tokens);
  }

  public static quadkey(template: string, coord: Coord, zoom: number, options: CommonOptions = {}): string {
    const { x, y } = this.normalizeCoord(coord, zoom, options);
    const q = this.toQuadKey(x, y, zoom);
    const tokens = {
      X: x,
      Y: y,
      Z: zoom,
      Q: q,
      S: this.pickSubdomain(x, y, zoom, options.subdomains),
      ...this.kvToUpper(options.extraParams),
    };

    const sanitizedTemplate = this.sanitizeTemplate(template);
    return this.replaceTokens(sanitizedTemplate, tokens);
  }

  public static wmts(template: string, coord: Coord, zoom: number, options: WMTSOptions = {}): string {
    const { x, y } = this.normalizeCoord(coord, zoom, options);
    const yOriginTopLeft = options.yOriginTopLeft ?? true;
    const tileRow = yOriginTopLeft ? y : this.flipY(y, zoom);
    const tileCol = x;
    const tileMatrixId = options.tileMatrixId ? options.tileMatrixId(zoom) : String(zoom);

    const tokens = {
      Z: zoom,
      TileMatrix: tileMatrixId,
      TileRow: tileRow,
      TileCol: tileCol,
      X: x, // in case the template uses XYZ placeholders
      Y: tileRow,
      S: this.pickSubdomain(tileCol, tileRow, zoom, options.subdomains),
      ...this.kvToUpper(options.extraParams),
    };

    const sanitizedTemplate = this.sanitizeTemplate(template);
    return this.replaceTokens(sanitizedTemplate, tokens);
  }





  //-------------------------------------------------------------------------------------------------
  // Helpers
  // These are helper functions to assist with tile URL generation
  //-------------------------------------------------------------------------------------------------

  /**
   * Calculates the world size (number of tiles) at a given zoom level.
   *
   * @param z - The zoom level.
   * @returns The size of the world in tiles (2^z) at the specified zoom level.
   */
  private static worldSize(z: number): number {
    return 1 << z; // 2^z
  }

  private static normalizeCoord(coord: Coord, zoom: number, options: CommonOptions): Coord {
    const size = this.worldSize(zoom);
    let x = coord.x;
    let y = coord.y;

    // Optionally wrap X horizontally
    if (options.wrapX !== false) {
      x = ((x % size) + size) % size;
    }

    // Clamp Y to [0, size-1] only if explicitly requested
    if (options.clampY) {
      y = Math.max(0, Math.min(size - 1, y));
    }

    // Optional zoom guards:
    // Some providers return empty tiles beyond maxZoom;
    // here we can choose to clamp or let it pass.
    //
    // As this utility class will be ideally used inside the google maps engine env
    // we will use its build defence mechanism for setting min and max zoom level
    // so these guards will be empty and used as a placeholder for future needs.
    //
    // Reference: google.maps.MapType interface.
    if (typeof options.maxZoom === 'number' && zoom > options.maxZoom) {

    }
    if (typeof options.minZoom === 'number' && zoom < options.minZoom) {

    }

    return { x, y };
  }

  /**
   * Converts a Y tile coordinate from XYZ (top-left origin) to TMS (bottom-left origin) format.
   *
   * @param y - The Y coordinate in XYZ format.
   * @param z - The zoom level.
   * @returns The Y coordinate in TMS format.
   */
  private static flipY(y: number, z: number): number {
    return this.worldSize(z) - 1 - y;
  }

  /**
   * Generates a quadkey string for a given tile coordinate (x, y) and zoom level (z).
   *
   * A quadkey is a unique identifier for a tile in a quadtree-based tile system,
   * commonly used in mapping applications such as Bing Maps. The quadkey encodes
   * the tile's position and zoom level into a string of digits, where each digit
   * represents a quadrant at a specific level of detail.
   *
   * @param x - The x-coordinate of the tile.
   * @param y - The y-coordinate of the tile.
   * @param z - The zoom level (level of detail).
   * @returns The quadkey string representing the tile's position at the given zoom level.
   */
  private static toQuadKey(x: number, y: number, z: number): string {
    let quadKey = '';

    for (let i = z; i > 0; i--) {
      let digit = 0;
      const mask = 1 << (i - 1);

      if ((x & mask) !== 0) digit += 1;
      if ((y & mask) !== 0) digit += 2;

      quadKey += digit.toString();
    }

    return quadKey;
  }

  /**
   * Selects a subdomain from the provided list based on the tile coordinates (x, y, z).
   *
   * This method is typically used for load balancing tile requests across multiple subdomains.
   * It calculates an index by summing the x, y, and z values, taking the modulus with the number
   * of subdomains, and returning the subdomain at that index.
   *
   * @param x - The x coordinate of the tile.
   * @param y - The y coordinate of the tile.
   * @param z - The zoom level of the tile.
   * @param subdomains - An optional array of subdomain strings to choose from.
   * @returns The selected subdomain string, or an empty string if no subdomains are provided.
   */
  private static pickSubdomain(x: number, y: number, z: number, subdomains?: string[]): string {
    if (!subdomains || subdomains.length === 0) {
      return '';
    }

    const idx = Math.abs((x + y + z) % subdomains.length);
    return subdomains[idx];
  }

  /**
   * Replaces tokens in the given template string with corresponding values from the provided object.
   *
   * Tokens in the template should be enclosed in curly braces (e.g., `{token}`).
   * If a token's value is `undefined`, the token is left unchanged in the result.
   *
   * @param template - The string containing tokens to be replaced.
   * @param values - An object mapping token names to their replacement values.
   * @returns The template string with tokens replaced by their corresponding values.
   */
  private static replaceTokens(template: string, values: Record<string, string | number | undefined>): string {
    return template.replace(/\{([A-Za-z0-9_]+)\}/g, (_match, key: string) => {
      const val = this.getValueCI(values, key);
      return val !== undefined
        ? String(val)
        : _match;
    });
  }

  // An alternative implementation of replaceTokens using an imperative approach
  private static replaceTokensImperative(template: string, values: Record<string, string | number | undefined>): string {
    let res = '';
    let i = 0;

    while (i < template.length) {
      const ch = template[i];

      if (ch === '{') {
        const close = template.indexOf('}', i + 1);
        if (close > i) {
          const key = template.slice(i + 1, close);
          const val = this.getValueCI(values, key);
          if (val !== undefined) {
            res += String(val);
          } else {
            res += template.slice(i, close + 1);
          }
          i = close + 1;
          continue;
        }
      }

      res += ch;
      i++;
    }

    return res;
  }


  /**
   * Returns a new object with all keys from the input object duplicated in uppercase.
   *
   * For each key-value pair in the input `kv`, the output object will contain both the original key and its uppercase version,
   * both mapping to the same value. This allows for case-insensitive access to the values using either the original or uppercase keys.
   *
   * @param kv - An optional record of string keys to string or number values.
   * @returns A new record containing both original and uppercase keys for each entry in `kv`.
   */
  private static kvToUpper(kv?: Record<string, string | number>): Record<string, string | number> {
    if (!kv) return {};

    const out: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(kv)) {
      out[k] = v;
      out[k.toUpperCase()] = v;
    }
    return out;
  }

  private static getValueCI(values: Record<string, string | number | undefined>, key: string): string | number | undefined {
    if (key in values) return values[key];

    const up = key.toUpperCase();
    if (up in values) return values[up];

    const low = key.toLowerCase();
    if (low in values) return values[low];

    return undefined;
  }

  private static sanitizeTemplate(template: string): string {
    let sanitizedTemplate = (template ?? '').trim();

    const strip = (str: string, ch: '"' | "'"): string => {
      if (str.startsWith(ch)) str = str.slice(1);
      if (str.endsWith(ch)) str = str.slice(0, -1);
      return str;
    };

    // Use OR-style stripping for each quote type independently
    sanitizedTemplate = strip(sanitizedTemplate, '"');
    sanitizedTemplate = strip(sanitizedTemplate, "'");

    return sanitizedTemplate;
  }
}


/**
 * Helper function to ensure compile-time type checking.
 * Returns the provided array of tile schemes as-is, preserving its readonly type.
 *
 * @typeParam T - A readonly tuple of `TileScheme` elements.
 * @param schemes - The array of tile schemes to allow.
 * @returns The same array of tile schemes, with its type preserved.
 */
function createAllowedSchemes<T extends readonly TileScheme[]>(schemes: T): T {
  return schemes;
}
